import React from 'react';

import { useMutation } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { getGetCardQueryKey } from '@vlabs/api/point-api/client';
import type { Linker } from '@vlabs/api/point-api/model';
import { queryClient } from '@vlabs/api/queryClient';
import { useModal } from '@vlabs/uikit';
import { toast } from 'react-toastify';

import { AddCardsToListForm } from '@vlabs/monokit/entities/lists';

import { useAttachCardToListTranslation } from './i18n';

export function useAttachCardsToList(
  cardIds: Linker['card_ids'],
  link: (body: Linker) => Promise<unknown>,
  cardsQueryKey: QueryKey,
): { open: VoidFunction; form: React.JSX.Element } {
  const { t } = useAttachCardToListTranslation();
  const modal = useModal({ size: 'small' });

  const moveCardsToListMutation = useMutation({
    mutationFn: async (attachListId: string) => {
      try {
        await link({
          action: 'attach',
          card_ids: cardIds,
          list_id: attachListId,
        });
      } catch (e) {
        toast.error(t('toast_error'));
        throw e;
      }

      toast.success(t('toast_success'));
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: cardsQueryKey }),
        ...cardIds.map(async (cardId) => queryClient.invalidateQueries({ queryKey: getGetCardQueryKey(cardId) })),
      ]);
      modal.close();
    },
  });

  return {
    open: modal.open,
    form: modal.wrap(<AddCardsToListForm
      onCancel={modal.close}
      onSubmit={moveCardsToListMutation.mutateAsync}
    />),
  };
}
